<template>
    <CqModal
        :showModal="show"
        heading="Diensten en stempels verwijderen"
        width="auto"
        maxWidth="50rem"
        :preventClosing="isSaving"
        @closeModal="closeModal"
    >
        <InfoGrid>
            <InfoHeader>Werknemer</InfoHeader>
            <div>{{ ResourceUtil.displayName(resource) ?? 'Onbekend' }}</div>
            <InfoHeader top>Te verwijderen</InfoHeader>
            <div class="toggles">
                <CqToggle v-model="deleteDeployments" :disabled="isSaving || !canDeleteDeployment" label="Diensten" />
                <CqToggle v-if="deleteDeployments" v-model="backToStampPhase" :disabled="isSaving" :label="'Terug naar stempelfase vanaf ' + formattedDay" />
                <CqToggle v-model="deleteStamps" :disabled="isSaving || !canDeleteStamp" label="Stempels" />
            </div>
        </InfoGrid>
        <CqButtonBar>
            <CqButton :disabled="isSaving" label="Annuleren" @click="closeModal" />
            <CqButton dangerous :disabled="isSaving || !(deleteDeployments || deleteStamps)" label="Ja, voor iedereen" @click="saveEveryone" />
            <CqButton primary v-if="resource" :disabled="isSaving || !(deleteDeployments || deleteStamps)" :label="'Ja, alleen voor ' + (ResourceUtil.displayName(resource) ?? 'Onbekend')" @click="saveSingle" />
        </CqButtonBar>
    </CqModal>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator';
    import {CqButton, CqButtonBar, CqModal, CqToggle} from '@cquential/ui';
    import {PlanbordResource, ResourceWithKeyedByDateObjects} from '@/types/api-object-extensions';
    import Notify from '@/components/modals/Notify.vue';
    import Dates from '@cquential/date-utils';
    import {ButtonColorScheme} from '@/types/enums/ButtonColorScheme';
    import {ApiClient, PersonalStamp} from '@cquential/api';
    import Loader from '@/components/modals/Loader.vue';
    import {RosterData} from '@/types/types';
    import GenericConfirmationModal from '@/components/modals/GenericConfirmationModal.vue';
    import {ResourceUtil} from '@/util/ResourceUtil';
    import InfoGrid from '@/components/Branding/InfoGrid.vue';
    import InfoHeader from '@/components/Branding/InfoHeader.vue';
    import GlobalErrorModal from '@/components/modals/GlobalErrorModal.vue';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqModal,
            CqToggle,
            InfoGrid,
            InfoHeader,
        },
        emits: [
            'reload:deployments',
            'reload:stamps',
            'reload:roster',
        ],
    })
    export default class DeploymentsStampsDeleteModal extends Vue {
        private static instance: DeploymentsStampsDeleteModal | null;

        public show: boolean = false;
        public isSaving: boolean = false;
        public canEdit: boolean = false;
        public deleteDeployments: boolean = false;
        public backToStampPhase: boolean = false;
        public deleteStamps: boolean = true;
        public resource: ResourceWithKeyedByDateObjects | null = null;
        public day: Date = new Date();
        public roster: RosterData | null = null;
        public ResourceUtil: typeof ResourceUtil = ResourceUtil;
        public ButtonColorScheme: typeof ButtonColorScheme = ButtonColorScheme;

        public mounted(): void {
            DeploymentsStampsDeleteModal.instance = this;
        }

        public beforeUnmount(): void {
            DeploymentsStampsDeleteModal.instance = null;
        }

        public get formattedDay(): string {
            return Dates.Formatter.formatDateLong(this.day);
        }

        public get canDeleteDeployment(): boolean {
            return this.canEdit && !!this.resource && !!this.day && this.day >= this.resource.salaryAcceptedUntil;
        }

        public get canDeleteStamp(): boolean {
            return this.canEdit && !!this.day;
        }

        public open(
            openResource: ResourceWithKeyedByDateObjects,
            openRoster: RosterData,
            openDay: Date,
            openDeleteDeployments: boolean = true,
            openDeleteStamps: boolean = false,
            openCanEdit: boolean = false,
        ): void {
            this.show = true;
            this.resource = openResource;
            this.roster = openRoster;
            this.day = openDay;
            this.deleteDeployments = openDeleteDeployments && this.canDeleteDeployment;
            this.deleteStamps = openDeleteStamps && this.canDeleteStamp;
            this.canEdit = openCanEdit;
        }

        private saveSingle(): void {
            this.handlePromises([this.deleteResourceDeployments(), this.deleteResourceStamps()]);
        }

        private saveEveryone(): void {
            const types: string[] = [];
            if (this.deleteDeployments) {
                types.push('diensten');
            }
            if (this.deleteStamps) {
                types.push('stempels');
            }

            this.closeModal();

            /** @see GenericConfirmationModal.openModal() */
            // @ts-expect-error TS2339
            GenericConfirmationModal.__vfdConstructor.openModal<'YES' | 'NO'>(
                'Diensten verwijderen voor iedereen',
                'Weet je echt zeker dat je alle ' + types.join(' en ') + ' vanaf ' + Dates.Formatter.formatDateLong(this.day) + ' wilt verwijderen voor iedereen?',
                [
                    {
                        value: 'NO',
                        text: 'Annuleer',
                    },
                    {
                        value: 'YES',
                        text: 'Ja, ik weet het zeker',
                        color: ButtonColorScheme.Orange,
                    },
                ],
                'NO',
            ).then((value: 'YES' | 'NO'): void => {
                if (value === 'YES') {
                    this.handlePromises([this.deleteRosterDeployments(), this.updateRosterStampsEndDate(), this.deleteRosterStamps()])
                        .then((): void => {
                            this.$emit('reload:roster');
                        })
                        .catch((): void => {
                            this.show = true;
                        });

                    return;
                }

                this.show = true;
            });
        }

        private handlePromises(promises: Promise<void>[]): Promise<void> {
            this.isSaving = true;

            return Promise.all(promises)
                .then((): void => {
                    this.closeModal();
                })
                .catch((): void => {
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'Er ging iets verkeerd. Probeer het opnieuw.', type: 'error'});
                })
                .finally((): void => {
                    this.isSaving = false;
                });
        }

        private deleteResourceDeployments(): Promise<void> {
            if (!this.deleteDeployments || !this.resource || !this.canDeleteDeployment) {
                return Promise.resolve();
            }

            if (this.backToStampPhase) {
                /** @see Loader.setPending() */
                // @ts-expect-error TS2339
                Loader.__vfdConstructor.setPending('roster');
            }

            return ApiClient.instance.deleteResourceDeployments(this.resource!.id!, this.day).then((): void => {
                if (this.backToStampPhase) {
                    ApiClient.instance.resetResourceStampDeployedUntil(this.resource!.id!, Dates.Utils.addDays(this.day, -1))
                        .then((): void => {
                            this.$emit('reload:roster');
                        })
                        /** @see GlobalErrorModal.globalErrorHandler() */
                        // @ts-expect-error TS2339
                        .catch(GlobalErrorModal.__vfdConstructor.globalErrorHandler);
                } else {
                    this.$emit('reload:deployments', this.resource!.id!);
                }
            });
        }

        private deleteResourceStamps(): Promise<void> {
            if (!this.deleteStamps || !this.resource || !this.canDeleteStamp) {
                return Promise.resolve();
            }

            return ApiClient.instance.deleteResourceStamps(this.resource.id!, this.day).then((): void => {
                this.$emit('reload:stamps', this.resource!.id!);
            });
        }

        private deleteRosterDeployments(): Promise<void> {
            if (!this.deleteDeployments || !this.canDeleteDeployment) {
                return Promise.resolve();
            }

            /** @see Loader.setPending() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setPending('roster');

            return ApiClient.instance.deleteRosterDeployments(this.roster!.rosterId, this.day).then((): void => {
                if (this.backToStampPhase) {
                    ApiClient.instance.resetRosterStampDeployedUntil(this.roster!.rosterId, Dates.Utils.addDays(this.day, -1))
                        .then((): void => {
                            this.$emit('reload:roster');
                        })
                        /** @see GlobalErrorModal.globalErrorHandler() */
                        // @ts-expect-error TS2339
                        .catch(GlobalErrorModal.__vfdConstructor.globalErrorHandler);
                } else {
                    this.$emit('reload:roster');
                }
            });
        }

        private updateRosterStampsEndDate(): Promise<void> {
            if (!this.deleteStamps || !this.canDeleteStamp) {
                return Promise.resolve();
            }

            const updatePersonalStamps: PersonalStamp[] = [];
            this.roster?.rosterResources.forEach((rosterResource: PlanbordResource): void => {
                rosterResource.personalStamps?.forEach((personalStamp: PersonalStamp): void => {
                    const dayBefore: Date = Dates.Utils.addDays(this.day, -1);
                    if (personalStamp.beginDate < dayBefore) {
                        personalStamp.endDate = dayBefore;
                        updatePersonalStamps.push(personalStamp);
                    }
                });
            });

            if (updatePersonalStamps.length > 0) {
                return ApiClient.instance.updatePersonalStamps(updatePersonalStamps);
            }

            return Promise.resolve();
        }

        private deleteRosterStamps(): Promise<void> {
            if (!this.deleteStamps || !this.canDeleteStamp) {
                return Promise.resolve();
            }

            const deletePersonalStamps: number[] = [];
            this.roster?.rosterResources.forEach((rosterResource: PlanbordResource): void => {
                rosterResource.personalStamps?.forEach((personalStamp: PersonalStamp): void => {
                    if (personalStamp.beginDate >= this.day) {
                        personalStamp.endDate = this.day;
                        deletePersonalStamps.push(personalStamp.id!);
                    }
                });
            });
            if (deletePersonalStamps.length > 0) {
                return ApiClient.instance.deletePersonalStamps(deletePersonalStamps);
            }

            return Promise.resolve();
        }

        public closeModal(): void {
            this.show = false;
        }

        public static open(openResource: ResourceWithKeyedByDateObjects, openRoster: RosterData, openDay: Date, openDeleteDeployments: boolean = true, openDeleteStamps: boolean = false, openCanEdit: boolean = false): void {
            this.instance!.open(openResource, openRoster, openDay, openDeleteDeployments, openDeleteStamps, openCanEdit);
        }
    }
</script>

<style lang="scss" scoped>
    .toggles {
        display: grid;
        gap: 0.5rem;
    }
</style>
